import _ from 'lodash';
import React, { Component } from 'react';
// TODO: Migrate module so that you don’t import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import Select from 'corp/components/UI/Select';
import { Label, Button } from 'design-system/Form';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import SelectPermissionRole from 'corp/components/UI/SelectPermissionRole';
import { SelectOption, renderSelectedObjects } from 'corp/components/UI/Select/helpers';
import { fetchUsersWithRole } from 'react-scale/containers/corp/corp-permissions/helpers';
import SelectScalien from 'corp/components/UI/SelectScalien';

interface IRevokeCorpPermissionRoleProps {
  showRole: boolean;
  usersWithRole: {
    email: string;
    _id: string;
  }[];
  corpPermissionRoleId?: string;
}

interface IRevokeCorpPermissionRoleState {
  corpPermissionRole?: SelectOption;
  usersWithRole: SelectOption[]; // only show options in this list
  selectedUsers?: SelectOption[];
  loading: boolean;
  displayLightSelector: boolean;
  message?: string;
  succeeded?: boolean;
}

export default class RevokeCorpPermissionRole extends Component<
  IRevokeCorpPermissionRoleProps,
  IRevokeCorpPermissionRoleState
> {
  initialState = {
    corpPermissionRole: this.props.corpPermissionRoleId
      ? { label: '', value: this.props.corpPermissionRoleId }
      : undefined,
    usersWithRole: this.props.usersWithRole?.map(user => ({ label: user.email, value: user._id })),
    selectedUsers: undefined,
    loading: false,
    displayLightSelector: false,
    message: undefined,
    succeeded: undefined,
  };

  constructor(props: IRevokeCorpPermissionRoleProps, context: $FIXME) {
    super(props, context);
    this.state = this.initialState;
  }

  static defaultProps = {
    showRole: true,
    usersWithRole: [],
  };

  onChangeCorpPermissionRole = async (corpPermissionRole?: SelectOption): Promise<void> => {
    this.setState({ loading: true });
    try {
      const usersWithRole = await fetchUsersWithRole(corpPermissionRole);
      this.setState({
        corpPermissionRole,
        usersWithRole,
        loading: false,
        message: undefined,
        displayLightSelector: false,
        succeeded: undefined,
      });
    } catch (error) {
      this.setState({
        corpPermissionRole,
        usersWithRole: [],
        loading: false,
        message: undefined,
        displayLightSelector: true,
        succeeded: undefined,
      });
    }
  };

  onSelectUser = (selectedUser?: SelectOption): void => {
    if (!selectedUser) return;
    const selectedUsers = this.state.selectedUsers
      ? _.uniqBy([...this.state.selectedUsers, selectedUser], 'value')
      : [selectedUser];
    this.setState({ selectedUsers, message: undefined, succeeded: undefined });
  };

  onReset = (): void => {
    this.setState({ ...this.initialState });
  };

  revokeCorpPermissionRole = async (event: React.MouseEvent<HTMLElement>): Promise<void> => {
    event.preventDefault();

    const isDisabled =
      this.state.loading || !this.state.corpPermissionRole || !this.state.selectedUsers;
    if (isDisabled) return;
    this.setState({ loading: true, message: undefined, succeeded: undefined });

    try {
      const corpPermissionRoleId = this.state.corpPermissionRole!.value;
      const userIds = _.uniq(this.state.selectedUsers!.map(option => option.value));

      const { success, retMessage } = await apiFetch(null, `corp-api/revoke_corp_permission_role`, {
        method: 'POST',
        body: {
          corpPermissionRoleId,
          userIds,
        },
      });

      this.setState({ succeeded: success, message: retMessage });
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    } finally {
      this.setState({ loading: false });
    }
  };

  renderRevokeCorpPermissionRole() {
    const corpRoleSummary = this.state.corpPermissionRole ? (
      <div>
        {`Found ${this.state.usersWithRole.length} corp users with role `}
        <a href={`lookup/${this.state.corpPermissionRole.value}`}>
          {this.state.corpPermissionRole.label}
        </a>
      </div>
    ) : (
      ''
    );

    return (
      <div className="container">
        {this.props.showRole && (
          <>
            <div className="text">
              This tool handles removing an existing corp permission role from a list of corp users.
              To use it, you need to be a privileged user (i.e. have <b>onsite</b> access role) for
              all projects/customers included in the permission role.
            </div>
            {this.state.displayLightSelector && (
              <div className="text">
                <br />
                <b> Warning </b>
                <br />A role with too many users was selected. The user selector has been simplified
                to improve performance. You can still search for users by email and roles will be
                removed from all selected users.
              </div>
            )}
            <div>
              <Label>Corp Permission Role</Label>
              <SelectPermissionRole
                value={this.state.corpPermissionRole}
                disabled={this.state.loading}
                onChange={this.onChangeCorpPermissionRole}
              />
              {corpRoleSummary}
            </div>
          </>
        )}
        <div>
          <Label>Corp Users</Label>
          <div>
            {this.state.displayLightSelector ? (
              <SelectScalien
                placeholder="Select corp users to remove this role from"
                disabled={this.state.loading || !this.state.corpPermissionRole}
                onChange={this.onSelectUser}
                filterOption={(option: SelectOption) =>
                  !_.find(this.state.usersWithRole, x => x.value === option.value)
                }
              />
            ) : (
              <Select
                placeholder="Select corp users to remove this role from"
                onChange={this.onSelectUser}
                disabled={
                  this.state.loading ||
                  !this.state.corpPermissionRole ||
                  !this.state.usersWithRole.length
                }
                options={this.state.usersWithRole}
              />
            )}
          </div>
          {renderSelectedObjects(this.state.selectedUsers)}
        </div>
        <div className="buttons">
          <Button
            primary
            type="button"
            disabled={
              this.state.loading || !this.state.corpPermissionRole || !this.state.selectedUsers
            }
            onClick={this.revokeCorpPermissionRole}
          >
            {this.state.loading ? 'Loading...' : 'Revoke Corp Permission Role'}
          </Button>
          &emsp;&emsp;
          <Button type="button" disabled={this.state.loading} onClick={this.onReset}>
            {this.state.loading ? 'Loading...' : 'Reset Form'}
          </Button>
        </div>
        {this.state.message && (
          <div className={this.state.succeeded ? 'successMessage' : 'errorMessage'}>
            {this.state.message}
          </div>
        )}
        <style jsx>{`
          .container {
            max-width: 1000px;
            min-height: 250px;
            padding-bottom: 20px;
            margin: 20px 20px;
            font-weight: normal;
          }
          .selector {
            margin: 20px 0;
          }
          .selector .title {
            font-size: 20px;
            margin: 10px 0;
          }
          h2.subtitle {
            font-size: 16px;
          }
          .buttons {
            margin-top: 12px;
            text-align: center;
          }
          .successMessage {
            margin-top: 12px;
            border: 1px solid rgba(0, 255, 0, 0.3);
            border-radius: 4px;
            background-color: rgba(0, 255, 0, 0.03);
            padding: 16px;
            color: green;
          }
          .errorMessage {
            margin-top: 12px;
            border: 1px solid rgba(255, 0, 0, 0.3);
            border-radius: 4px;
            background-color: rgba(255, 0, 0, 0.03);
            padding: 16px;
            color: brown;
          }
        `}</style>
      </div>
    );
  }

  render() {
    return <div id="body">{this.renderRevokeCorpPermissionRole()}</div>;
  }
}
